import { useCallback, useEffect, useRef, useState } from "react";

type DraftState<T> = {
  values: T;
  setValues: React.Dispatch<React.SetStateAction<T>>;
  restored: boolean;
  savedAt: number | null;
  clearDraft: () => void;
  hydrated: boolean;
};

/**
 * Persists form values to localStorage (debounced) so a refresh or accidental
 * navigation doesn't lose typed input.
 */
export function useFormDraft<T extends Record<string, string>>(
  storageKey: string,
  initialValues: T,
  { debounceMs = 500, maxAgeMs = 1000 * 60 * 60 * 24 * 7 } = {}
): DraftState<T> {
  const [values, setValues] = useState<T>(initialValues);
  const [restored, setRestored] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Restore after mount only, so SSR markup and first render stay identical.
  useEffect(() => {
    setHydrated(true);
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { savedAt?: number; values?: Partial<T> };
      if (!parsed?.values) return;
      if (parsed.savedAt && Date.now() - parsed.savedAt > maxAgeMs) {
        window.localStorage.removeItem(storageKey);
        return;
      }
      const merged = { ...initialValues } as T;
      let hasContent = false;
      for (const key of Object.keys(initialValues) as Array<keyof T>) {
        const value = parsed.values[key];
        if (typeof value === "string") {
          merged[key] = value as T[keyof T];
          if (value.trim() !== "") hasContent = true;
        }
      }
      if (!hasContent) return;
      setValues(merged);
      setRestored(true);
      setSavedAt(parsed.savedAt ?? null);
    } catch {
      // Corrupt draft: ignore and start fresh.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Debounced save.
  useEffect(() => {
    if (!hydrated) return;
    const hasContent = Object.values(values).some((v) => v.trim() !== "");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      try {
        if (!hasContent) {
          window.localStorage.removeItem(storageKey);
          setSavedAt(null);
          return;
        }
        const stamp = Date.now();
        window.localStorage.setItem(storageKey, JSON.stringify({ savedAt: stamp, values }));
        setSavedAt(stamp);
      } catch {
        // Storage full or blocked: autosave is best-effort.
      }
    }, debounceMs);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [values, hydrated, storageKey, debounceMs]);

  const clearDraft = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
    setValues(initialValues);
    setRestored(false);
    setSavedAt(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  return { values, setValues, restored, savedAt, clearDraft, hydrated };
}
